
import { Component, Vue } from 'vue-property-decorator'
import { Customer } from '@/models'

type ConnectionStatus = 'all' | 'connected' | 'notConnected'

const PAGE_SIZE = 1000
const DISPLAY_STEP = 20

@Component({
  name: 'NativeAppUsers',
  components: {
    NativeAppUsersHeader: () => import('./components/NativeAppUsersHeader.vue'),
    NativeAppUsersStats: () => import('./components/NativeAppUsersStats.vue')
  }
})
export default class extends Vue {
  allCustomers: Customer[] = []
  displayLimit = DISPLAY_STEP
  loading = false

  listQuery = {
    search: '',
    connectionStatus: 'all' as ConnectionStatus
  }

  get connectionStatusOptions(): { value: ConnectionStatus; label: string }[] {
    return [
      { value: 'all', label: this.$t('nativeAppUsers.filters.all') as string },
      { value: 'connected', label: this.$t('nativeAppUsers.filters.connected') as string },
      { value: 'notConnected', label: this.$t('nativeAppUsers.filters.notConnected') as string }
    ]
  }

  get totalCustomers(): number {
    return this.allCustomers.length
  }

  get nativeUsersCount(): number {
    return this.allCustomers.filter((customer) => customer.hasNativeSignIn === true).length
  }

  get recentConnectionsCount(): number {
    const threshold = this.$moment().subtract(30, 'days')
    return this.allCustomers.filter((customer) => {
      if (!customer.hasNativeSignIn || !customer.lastNativeSignInAt) return false
      return this.$moment(customer.lastNativeSignInAt).valueOf() >= threshold.valueOf()
    }).length
  }

  get filteredCustomers(): Customer[] {
    let result = [...this.allCustomers]

    if (this.listQuery.connectionStatus === 'connected') {
      result = result.filter((customer) => customer.hasNativeSignIn === true)
    } else if (this.listQuery.connectionStatus === 'notConnected') {
      result = result.filter((customer) => customer.hasNativeSignIn !== true)
    }

    const search = this.listQuery.search.trim().toLowerCase()
    if (search) {
      result = result.filter((customer) => {
        const email = customer.email?.toLowerCase() || ''
        const firstname = customer.firstname?.toLowerCase() || ''
        const lastname = customer.lastname?.toLowerCase() || ''
        return email.includes(search) || firstname.includes(search) || lastname.includes(search)
      })
    }

    return result.sort((a, b) => this.compareByLastNativeSignIn(a, b))
  }

  get totalFilteredCustomers(): number {
    return this.filteredCustomers.length
  }

  get displayedCustomers(): Customer[] {
    return this.filteredCustomers.slice(0, this.displayLimit)
  }

  created(): void {
    this.refresh()
  }

  refresh(): void {
    this.displayLimit = DISPLAY_STEP
    this.fetchAllCustomers()
  }

  applyFilters(): void {
    this.displayLimit = DISPLAY_STEP
  }

  loadMore(): void {
    this.displayLimit += DISPLAY_STEP
  }

  async fetchAllCustomers(): Promise<void> {
    this.loading = true
    this.allCustomers = []

    try {
      let page = 1
      let hasMore = true

      while (hasMore) {
        const { data } = await Customer
          .includes(['program', 'location'])
          .page(page)
          .per(PAGE_SIZE)
          .order({ createdAt: 'desc' })
          .all()

        this.allCustomers.push(...data)
        hasMore = data.length === PAGE_SIZE
        page++
      }
    } finally {
      this.loading = false
    }
  }

  compareByLastNativeSignIn(a: Customer, b: Customer): number {
    if (!a.lastNativeSignInAt && !b.lastNativeSignInAt) return 0
    if (!a.lastNativeSignInAt) return 1
    if (!b.lastNativeSignInAt) return -1
    return this.$moment(b.lastNativeSignInAt).valueOf() - this.$moment(a.lastNativeSignInAt).valueOf()
  }

  formatName(customer: Customer): string {
    if (customer.firstname && customer.lastname) {
      return `${customer.firstname} ${customer.lastname}`
    }
    return this.$t('customers.card.notSpecified') as string
  }

  formatLastNativeSignInAt(date: string): string {
    return this.$moment(date).format('DD/MM/YYYY [à] HH:mm')
  }
}
